import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";

import React, { useEffect, useMemo, useState } from "react";

import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import useAxios from "axios-hooks";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  ViewIcon,
  DeleteIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import {
  FaMapMarkerAlt,
  FaChargingStation,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { FaTowerBroadcast } from "react-icons/fa6";
import { motion } from "framer-motion";

// Custom components
import Card from "components/card/Card";
import { deleteModalState } from "atoms/deleteModalState";
import DeleteModal from "components/modals/DeleteModal";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={FaChargingStation} boxSize={16} color="brand.500" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Charging Stations Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no charging stations available at the moment. New stations will
      appear here once added.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, icon, label;

  switch (status.toLowerCase()) {
    case "active":
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
      break;
    case "inactive":
      color = "red";
      icon = WarningIcon;
      label = "Inactive";
      break;
    default:
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const LocationAvatar = ({ name }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <Avatar
      name={name}
      size="sm"
      bg="brand.500"
      color="white"
      fontWeight="bold"
      getInitials={() => initials}
    />
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "brand" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

export default function LocationTable(props) {
  const { columnsData } = props;
  const [showDeleteModal, setShowDeleteModal] =
    useRecoilState(deleteModalState);
  const [selectedStation, setSelectedStation] = useState(null);
  const [chargingStations, setChargingStations] = useState([]);
  const loggerInUser = useRecoilValue(loggedInUserState);
  const navigate = useNavigate();
  const [{ data: stationData, loading }, getStations] = useAxios(
    { method: "GET", url: `/api/locations` },
    { manual: true },
  );

  const [searchQuery, setSearchQuery] = useState("");
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => chargingStations, [chargingStations]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const name = row.original.Name || "";
          const uid = row.original.Uid || "";
          const city = row.original.City || "";
          const state = row.original.State || "";
          const postalCode = row.original.PostalCode || "";
          const address = row.original.Address || "";

          return (
            name.toLowerCase().includes(lowercasedQuery) ||
            uid.toLowerCase().includes(lowercasedQuery) ||
            city.toLowerCase().includes(lowercasedQuery) ||
            state.toLowerCase().includes(lowercasedQuery) ||
            postalCode.toLowerCase().includes(lowercasedQuery) ||
            address.toLowerCase().includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
  } = tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "brand.500";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const getChargingStations = async () => {
    try {
      const response = await getStations();
      logger.log(response.data.data);
      setChargingStations(response.data.data);
    } catch (error) {
      logger.log(error);
    }
  };

  const handleDeleteClick = (station) => {
    setSelectedStation(station);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setSelectedStation(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    getChargingStations();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  useEffect(() => {
    getChargingStations();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get time since update
  const getTimeSince = (dateString) => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return interval + " years ago";

    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return interval + " months ago";

    interval = Math.floor(seconds / 86400);
    if (interval > 1) return interval + " days ago";

    interval = Math.floor(seconds / 3600);
    if (interval > 1) return interval + " hours ago";

    interval = Math.floor(seconds / 60);
    if (interval > 1) return interval + " minutes ago";

    return "Just now";
  };

  return loading ? (
    <LoadingSpinner />
  ) : chargingStations.length > 0 ? (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        {/* Header with search */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Locations
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, UID, city, state, postal code..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Locations
              </Text>
              <Badge ml={2} colorScheme="brand" borderRadius="full" px={2}>
                {chargingStations.length} total
              </Badge>
            </Flex>
          </Flex>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table {...getTableProps()} variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Name") {
                        data = (
                          <Flex align="center" gap={3}>
                            <LocationAvatar name={cell.row.original.Name} />
                            <Box>
                              <Text color={textColor} fontWeight="semibold">
                                {cell.row.original.Name}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {cell.row.original.City},{" "}
                                {cell.row.original.State}
                              </Text>
                            </Box>
                          </Flex>
                        );
                      } else if (cell.column.Header === "PinCode") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text color={textColor} fontSize="sm">
                              {cell.row.original.PostalCode || "N/A"}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Location Type") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              Public
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Status") {
                        data = (
                          <Flex align="center" justify="center">
                            <StatusBadge status="active" />
                          </Flex>
                        );
                      } else if (cell.column.Header === "Roaming") {
                        const isRoamingEnabled =
                          cell.row.original.IsOcpiEnabled;
                        data = (
                          <Flex align="center" justify="center">
                            <Tooltip
                              label={
                                isRoamingEnabled
                                  ? "OCPI Roaming Enabled"
                                  : "OCPI Roaming Disabled"
                              }
                              hasArrow
                              placement="top"
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                p={2}
                                borderRadius="full"
                                bg={
                                  isRoamingEnabled
                                    ? "rgba(35, 169, 121, 0.08)"
                                    : "rgba(160, 174, 192, 0.08)"
                                }
                                cursor="pointer"
                                onClick={(e) => {
                                  if (loggerInUser.role !== "uadmin") {
                                    return;
                                  }
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/locations/view/${cell.row.original.Id}/roaming`,
                                  );
                                }}
                                _hover={{
                                  transform: "scale(1.1)",
                                  boxShadow:
                                    "0 4px 12px rgba(35, 169, 121, 0.2)",
                                }}
                                transition="all 0.2s ease"
                              >
                                <Icon
                                  as={FaTowerBroadcast}
                                  boxSize={4}
                                  color={
                                    isRoamingEnabled ? "brand.500" : "gray.400"
                                  }
                                  transition="all 0.3s ease"
                                />
                              </Box>
                            </Tooltip>
                          </Flex>
                        );
                      } else if (cell.column.Header === "No.of CP") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              bg={themeColor}
                              color="white"
                              borderRadius="full"
                              px={3}
                              py={1}
                              fontWeight="bold"
                            >
                              1
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Availability") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme="green"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              Available
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Last Updated") {
                        const updatedAt =
                          cell.row.original.UpdatedAt ||
                          new Date().toISOString();
                        data = (
                          <Tooltip
                            label={formatDate(updatedAt)}
                            placement="top"
                            hasArrow
                          >
                            <Flex align="center" justify="center">
                              <Icon as={TimeIcon} mr={1} color="gray.500" />
                              <Text color={textColor} fontSize="xs">
                                {getTimeSince(updatedAt)}
                              </Text>
                            </Flex>
                          </Tooltip>
                        );
                      } else if (cell.column.Header === "Actions") {
                        const role = loggerInUser?.role;
                        const accessLevel = accessLevels[role]?.accessLevel;
                        const isDisabled = accessLevel > 2;
                        data = (
                          <Flex align="center" justify="center" gap={2}>
                            <Tooltip
                              label="View Details"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<ViewIcon />}
                                colorScheme="blue"
                                variant="ghost"
                                size="sm"
                                borderRadius="full"
                                aria-label="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/locations/view/${cell.row.original.Id}/overview`,
                                  );
                                }}
                              />
                            </Tooltip>

                            <RoleBasedRender roles={onlyAdmins}>
                              <Tooltip
                                label="Delete Location"
                                placement="top"
                                hasArrow
                              >
                                <IconButton
                                  icon={<DeleteIcon />}
                                  colorScheme="red"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="Delete"
                                  isDisabled={isDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(cell.row.original);
                                  }}
                                />
                              </Tooltip>
                            </RoleBasedRender>

                            <Popover placement="left" closeOnBlur={true}>
                              <PopoverTrigger>
                                <IconButton
                                  icon={<InfoIcon />}
                                  colorScheme="gray"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="More Info"
                                />
                              </PopoverTrigger>
                              <PopoverContent>
                                <PopoverArrow />
                                <PopoverCloseButton />
                                <PopoverHeader fontWeight="bold">
                                  Location Details
                                </PopoverHeader>
                                <PopoverBody>
                                  <VStack align="start" spacing={2}>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Address:
                                      </Text>{" "}
                                      {cell.row.original.Address}
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        UID:
                                      </Text>{" "}
                                      {cell.row.original.Uid}
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Country:
                                      </Text>{" "}
                                      {cell.row.original.Country}
                                    </Text>
                                    <Button
                                      size="xs"
                                      rightIcon={<ExternalLinkIcon />}
                                      colorScheme="brand"
                                      variant="outline"
                                      onClick={() =>
                                        navigate(
                                          `/admin/locations/view/${cell.row.original.Id}/overview`,
                                        )
                                      }
                                    >
                                      Full Details
                                    </Button>
                                  </VStack>
                                </PopoverBody>
                              </PopoverContent>
                            </Popover>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {chargingStations.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        data={selectedStation}
        title="locations"
      />
    </MotionBox>
  ) : (
    <GlassMorphismCard w="100%" py={8}>
      <EmptyStateIllustration />
    </GlassMorphismCard>
  );
}
